import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'burningteam_kalender_onboarding_v1';

const slides = [
  {
    gradient: 'from-emerald-500 via-teal-500 to-cyan-600',
    emoji: '📅',
    title: 'Willkommen im Zielplanungs-Kalender!',
    subtitle: 'Dein persönliches Planungs-Kraftwerk',
    intro: 'Hier planst du nicht nur deinen Alltag – du gestaltest aktiv deine Zukunft. Mit 6 leistungsstarken Werkzeugen, die zusammen funktionieren.',
    features: [
      { icon: '🗓️', text: 'Kalender (Jahr / Monat / Woche)' },
      { icon: '🎯', text: 'Tagesfokus – was zählt wirklich heute?' },
      { icon: '✅', text: 'To-dos in vier Zeitebenen' },
      { icon: '🔄', text: 'Habit Tracker für neue Routinen' },
      { icon: '🏆', text: 'Zielplanung mit Fortschritts-EXP' },
    ],
    study: {
      text: 'Menschen, die ihre Zeit strukturiert planen, sind laut einer Studie der Harvard Business School um bis zu 56 % produktiver als unstrukturierte Personen.',
      source: 'Harvard Business School, 2021'
    }
  },
  {
    gradient: 'from-blue-500 via-indigo-500 to-purple-600',
    emoji: '🗓️',
    title: 'Jahres-, Monats- & Wochenansicht',
    subtitle: 'Dein strukturierter Kalender auf allen Ebenen',
    intro: 'Jede Zeitebene hat ihre eigene Ansicht – und zu jeder gibt es eine übersichtliche Listenansicht für eine strukturierte Übersicht. Die Wochenansicht ist in Zeitblöcke unterteilt, damit du fokussiert und ohne Ablenkung arbeitest.',
    features: [
      { icon: '📆', text: 'Jahresansicht: Überblick über alle Monate' },
      { icon: '📋', text: 'Monatsansicht: Termine im klassischen Kalender' },
      { icon: '⏱️', text: 'Wochenansicht: strukturierte Zeitblöcke pro Tag' },
      { icon: '📝', text: 'Listenansicht: strukturierte Übersicht' },
      { icon: '👥', text: 'Kunden-Termine werden direkt hier gespeichert' },
    ],
    study: {
      text: 'Eine Studie der University of California Irvine zeigt: Es dauert durchschnittlich 23 Minuten, um nach einer Ablenkung wieder in den Fokus zu finden. Wer seinen Tag in feste Zeitblöcke einteilt, reduziert Unterbrechungen drastisch und steigert die Produktivität um bis zu 40 %.',
      source: 'University of California Irvine, Gloria Mark, 2008 · Cal Newport, Deep Work, 2016'
    }
  },
  {
    gradient: 'from-amber-500 via-orange-500 to-red-500',
    emoji: '🎯',
    title: 'Tagesfokus',
    subtitle: 'Das Wichtigste – jeden Tag klar vor Augen',
    intro: 'Du kannst täglich eintragen, worauf du deinen Fokus legen solltest. Ein einfaches, kraftvolles Werkzeug, das den Unterschied macht.',
    features: [
      { icon: '📌', text: 'Täglich einen klaren Hauptfokus definieren' },
      { icon: '🧠', text: 'Reduziert mentalen Overload und Ablenkungen' },
      { icon: '⚡', text: 'Maximiert deine Energie auf das Wesentliche' },
      { icon: '📈', text: 'Macht sichtbar, was du jeden Tag bewegt hast' },
    ],
    study: {
      text: 'Personen, die täglich ihre Top-3-Prioritäten aufschreiben, erledigen laut einer Stanford-Studie 43 % mehr relevante Aufgaben als Personen ohne diese Gewohnheit.',
      source: 'Stanford University Behavioral Lab, 2018'
    }
  },
  {
    gradient: 'from-teal-500 via-emerald-500 to-green-600',
    emoji: '✅',
    title: 'To-dos & Aufgaben',
    subtitle: 'Vier Zeitebenen – ein System',
    intro: 'Trage Aufgaben in Jahres-, Monats-, Wochen- oder Tageslisten ein. Hake sie ab oder übertrage sie als Termin direkt in den Kalender.',
    features: [
      { icon: '📅', text: 'To-dos für Jahr / Monat / Woche / Tag' },
      { icon: '☑️', text: 'Einfaches Abhaken oder Übertragen als Termin' },
      { icon: '🔁', text: 'Aufgaben auf die nächste Ebene übertragen' },
      { icon: '👤', text: 'Kunden-Aufgaben werden hier ebenfalls gespeichert' },
      { icon: '🔗', text: 'Verknüpft mit dem Kalender – alles an einem Ort' },
    ],
    study: {
      text: 'Nutzer des GTD-Systems (Getting Things Done) berichten laut einer Studie von 40 % weniger Stress und 70 % besserer Aufgabenerledigung im Alltag.',
      source: 'GTD Impact Study, David Allen Company, 2020'
    }
  },
  {
    gradient: 'from-violet-500 via-purple-500 to-fuchsia-600',
    emoji: '🔄',
    title: 'Habit Tracker',
    subtitle: 'Neue Routinen mit Leichtigkeit aufbauen',
    intro: 'Der Routinen-Tracker hilft dir, neue Gewohnheiten in deinen Alltag zu integrieren – und das täglich abzuhaken. Auch für Schichtarbeiter optimiert!',
    features: [
      { icon: '📋', text: 'Routinen anlegen und täglich abhaken' },
      { icon: '🌙', text: 'Schichtarbeiter-Modus: flexible Tages-Zuweisung' },
      { icon: '🔥', text: 'Streaks motivieren zum Dranbleiben' },
      { icon: '📊', text: 'Statistiken zeigen deinen Fortschritt' },
      { icon: '🧬', text: 'Kleine tägliche Handlungen – große Wirkung' },
    ],
    study: {
      text: 'Laut einer UCL-Studie dauert es durchschnittlich 66 Tage, um eine Gewohnheit zu festigen. Wer seinen Fortschritt täglich trackt, bleibt laut James Clear (Atomic Habits) mit 2× höherer Wahrscheinlichkeit am Ball.',
      source: 'University College London & James Clear, Atomic Habits, 2018'
    }
  },
  {
    gradient: 'from-emerald-500 via-green-500 to-teal-600',
    emoji: '🏆',
    title: 'Mein Ziel',
    subtitle: 'Dein Jahresziel – schriftlich festgehalten',
    intro: 'Im Tab "Ziele" gibst du dein persönliches Jahresziel ein, definierst Meilensteine und hältst alles schriftlich fest. Das allein ist schon ein entscheidender Schritt Richtung Erfolg.',
    features: [
      { icon: '✍️', text: 'Jahresziel klar und konkret formulieren' },
      { icon: '🏅', text: 'Meilensteine definieren für sichtbaren Fortschritt' },
      { icon: '💡', text: 'Gewohnheiten & Hindernisse vorausdenken' },
      { icon: '⚡', text: 'Fortschritt-EXP steigt mit jedem abgehakten Meilenstein' },
    ],
    study: {
      text: 'Laut einer Studie der Dominican University of California steigt die Wahrscheinlichkeit, ein Ziel zu erreichen, um 42 %, wenn man es schriftlich festhält – und um weitere 33 %, wenn man den Fortschritt regelmäßig dokumentiert.',
      source: 'Dominican University of California, Dr. Gail Matthews, 2015'
    }
  },
  {
    gradient: 'from-cyan-500 via-teal-600 to-emerald-700',
    emoji: '🎯',
    title: 'Zielplanung mit Assistent',
    subtitle: 'Vom großen Ziel zur konkreten Wochenaufgabe',
    intro: 'Der Zielplanungs-Assistent bricht dein Jahresziel automatisch in monatliche und wöchentliche Etappen herunter – damit du jeden Tag weißt, was zu tun ist.',
    features: [
      { icon: '🤖', text: 'Assistent berechnet Monats- & Wochenziele automatisch' },
      { icon: '📅', text: 'Zieldatum wählbar – realistisch auf dich zugeschnitten' },
      { icon: '📆', text: 'Wochen-, Monats- & Jahresziele in den jeweiligen Ansichten sichtbar' },
      { icon: '✅', text: 'Jede Woche Fortschritt abhaken – dieses Gefühl? Unbezahlbar. 🤤' },
      { icon: '📊', text: 'Fortschrittsbalken oben immer sichtbar – dein Zielerreichungsgrad auf einen Blick' },
      { icon: '🏅', text: 'Bei Meilenstein Check per Klick abhaken – Schritt für Schritt zum Ziel' },
    ],
    study: {
      text: 'Wer Ziele in wöchentliche & monatliche Meilensteine aufteilt, erreicht sie laut der American Psychological Association mit 76 % höherer Wahrscheinlichkeit. Kleine, regelmäßige Schritte schlagen große, seltene Aktionen jedes Mal.',
      source: 'American Psychological Association, 2019'
    },
  },
  {
    gradient: 'from-emerald-500 via-teal-500 to-green-600',
    emoji: '💪',
    title: 'Bereit? Dann nur noch eins:',
    subtitle: 'Das Game beginnt jetzt.',
    intro: 'Gib jetzt als erstes dein Ziel ein – und das Game beginnt!',
    features: [
      { icon: '✍️', text: 'Ziel eingeben' },
      { icon: '🤖', text: 'Zielplanungs-Assistent startet automatisch' },
      { icon: '🚀', text: 'Deine Reise zum Ziel beginnt – Schritt für Schritt' },
    ],
    study: {
      text: 'Der wichtigste Schritt ist der erste. Wer heute beginnt, ist morgen schon einen Schritt weiter als alle, die noch warten.',
      source: 'Eigene Weisheit, die du täglich bestätigen wirst 😉'
    },
    isFinal: true,
  },
];

export default function ZielkalenderOnboarding({ open, onClose, onNavigateTo }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  if (!open) return null;

  const slide = slides[step];
  const isLast = step === slides.length - 1;
  const isFirst = step === 0;

  const handleFinish = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    onClose();
    // Zum Ziele-Tab navigieren
    if (onNavigateTo) onNavigateTo('goals');
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Overlay – X-Button schließt ohne Tab-Wechsel */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { localStorage.setItem(STORAGE_KEY, '1'); onClose(); }} />

      {/* Modal */}
      <div className="relative w-full sm:max-w-md sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col"
        style={{ maxHeight: '92vh' }}>

        {/* Gradient Header */}
        <div className={`bg-gradient-to-br ${slide.gradient} px-5 pt-6 pb-8 relative`}>
          {/* Close Button – schließt ohne Tab-Navigation */}
          <button onClick={() => { localStorage.setItem(STORAGE_KEY, '1'); onClose(); }}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors">
            <X size={20} />
          </button>

          {/* Step Dots */}
          <div className="flex justify-center gap-1.5 mb-5">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setStep(i)}
                className={`rounded-full transition-all ${
                  i === step ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/40'
                }`} />
            ))}
          </div>

          {/* Icon & Title */}
          <div className="text-center">
            <div className="text-5xl mb-3">{slide.emoji}</div>
            <h2 className="text-xl font-bold text-white leading-tight mb-1">{slide.title}</h2>
            <p className="text-white/80 text-sm font-medium">{slide.subtitle}</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white flex-1 overflow-y-auto">
          <div className="px-5 py-4">
            {/* Intro Text */}
            <p className="text-slate-600 text-sm leading-relaxed mb-4">{slide.intro}</p>

            {/* Features */}
            <div className="space-y-2 mb-4">
              {slide.features.map((f, i) => (
                <div key={i} className="flex items-start gap-3 bg-slate-50 rounded-xl px-3 py-2.5">
                  <span className="text-base flex-shrink-0 mt-0.5">{f.icon}</span>
                  <span className="text-sm text-slate-700 font-medium">{f.text}</span>
                </div>
              ))}
            </div>

            {/* Study Box */}
            <div className={`rounded-xl p-3.5 mb-2`}
              style={{ background: 'rgba(16,185,129,0.07)' }}>
              <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider mb-1.5">
                Wissenschaft sagt
              </p>
              <p className="text-xs text-slate-700 leading-relaxed italic">"{slide.study.text}"</p>
              <p className="text-[10px] text-slate-400 mt-1.5 font-medium">— {slide.study.source}</p>
            </div>

            {/* CTA Banner auf dem letzten Slide */}
            {slide.isFinal && (
              <div className="mt-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-4 text-center">
                <p className="text-white font-bold text-sm mb-0.5">Bereit? Dann nur noch eins:</p>
                <p className="text-white/90 text-xs">Gib als erstes <strong>dein Ziel</strong> ein – und das Game beginnt! 💪</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="bg-white border-t border-slate-100 px-5 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={isFirst}
            className={`flex items-center gap-1.5 text-sm font-medium transition-all ${
              isFirst ? 'text-slate-300 cursor-default' : 'text-slate-500 hover:text-slate-700'
            }`}>
            <ChevronLeft size={16} /> Zurück
          </button>

          <span className="text-xs text-slate-400 font-medium">{step + 1} / {slides.length}</span>

          {isLast ? (
            <button
              onClick={handleFinish}
              className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all active:scale-95">
              Ziel eingeben & starten 💪
            </button>
          ) : (
            <button
              onClick={() => setStep(s => Math.min(slides.length - 1, s + 1))}
              className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors">
              Weiter <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export { STORAGE_KEY as KALENDER_ONBOARDING_KEY };
